import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { List } from 'react-window';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import usePlayerStore from '../store/playerStore';
import useAuthStore from '../store/authStore';
import useSortable from '../hooks/useSortable';
import { loveTrack, updateTrack, findOrCreateArtist, findOrCreateAlbum, findOrCreateGenre, getPlaylists, addTrackToPlaylist, addTracksBulkToPlaylist, deleteTrack, removeTrackFromPlaylist, createPlaylist } from '../api';
import PlaylistPicker from './PlaylistPicker';

const LAST_PLAYLIST_KEY = 'musicapp_last_playlists';

function getLastPlaylists() {
  try { return JSON.parse(localStorage.getItem(LAST_PLAYLIST_KEY)) || []; } catch { return []; }
}

function saveLastPlaylists(playlist, current) {
  try {
    const updated = [playlist, ...current.filter(p => p.id !== playlist.id)].slice(0, 3);
    localStorage.setItem(LAST_PLAYLIST_KEY, JSON.stringify(updated));
  } catch {}
}

const EDITABLE_KEYS = ['title', 'artist_name', 'album_title', 'track_number', 'genre_name', 'year'];

const ALL_COLUMNS = [
  { key: 'index',            label: '#',          default: true,  width: 40,  sortKey: null,           render: (t, i, cur) => cur?.id === t.id ? '♪' : i + 1 },
  { key: 'title',            label: 'Title',       default: true,  width: 220, sortKey: 'title',        render: t => t.title, bold: true },
  { key: 'artist_name',      label: 'Artist',      default: true,  width: 160, sortKey: 'artist_name',  render: t => t.artist_name },
  { key: 'album_title',      label: 'Album',       default: true,  width: 160, sortKey: 'album_title',  render: t => t.album_title },
  { key: 'track_number',     label: 'Tr#',         default: true,  width: 50,  sortKey: 'track_number', render: t => t.track_number || '—' },
  { key: 'genre_name',       label: 'Genre',       default: true,  width: 120, sortKey: 'genre_name',   render: t => t.genre_name || '—' },
  { key: 'year',             label: 'Year',        default: true,  width: 60,  sortKey: 'year',         render: t => t.year || '—' },
  { key: 'duration_display', label: 'Time',        default: true,  width: 60,  sortKey: 'duration',     render: t => t.duration_display },
  { key: 'rating',           label: 'Rating',      default: false, width: 80,  sortKey: 'rating',       render: t => t.rating ? '★'.repeat(Math.round(t.rating / 20)) : '—' },
  { key: 'play_count',       label: 'Plays',       default: true,  width: 60,  sortKey: 'play_count',   render: t => t.play_count },
  { key: 'skip_count',       label: 'Skips',       default: false, width: 60,  sortKey: 'skip_count',   render: t => t.skip_count },
  { key: 'bpm',              label: 'BPM',         default: false, width: 60,  sortKey: 'bpm',          render: t => t.bpm || '—' },
  { key: 'bit_rate',         label: 'Bit Rate',    default: false, width: 80,  sortKey: 'bit_rate',     render: t => t.bit_rate ? `${t.bit_rate}k` : '—' },
  { key: 'file_format',      label: 'Format',      default: false, width: 70,  sortKey: 'file_format',  render: t => t.file_format?.toUpperCase() || '—' },
  { key: 'date_added',       label: 'Date Added',  default: false, width: 100, sortKey: 'date_added',   render: t => t.date_added ? t.date_added.slice(0, 10) : '—' },
  { key: 'last_played',      label: 'Last Played', default: true,  width: 100, sortKey: 'last_played',  render: t => t.last_played ? t.last_played.slice(0, 10) : '—' },
  { key: 'composer',         label: 'Composer',    default: false, width: 140, sortKey: 'composer',     render: t => t.composer || '—' },
  { key: 'is_loved',         label: '♥',           default: true,  width: 40,  sortKey: 'is_loved',     render: null },
  { key: 'delete',           label: '',            default: true,  width: 60,  sortKey: null,           render: null },
];

// Map frontend sortKey → Django ordering field name
const SORT_KEY_MAP = {
  artist_name: 'artist__name',
  album_title: 'album__title',
  genre_name: 'genre__name',
};

const STORAGE_KEY = 'musicapp_columns_v2';
const ROW_HEIGHT = 36;
const ROW_HEIGHT_MOBILE = 54;

const LOCKED_KEYS = ['index', 'title'];

function loadColumns(storageKey = STORAGE_KEY, defaults = null) {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure locked columns are always present
      const withLocked = [...new Set([...LOCKED_KEYS, ...parsed])];
      return ALL_COLUMNS.map(c => c.key).filter(k => withLocked.includes(k));
    }
  } catch {}
  return defaults || ALL_COLUMNS.filter(c => c.default).map(c => c.key);
}

function saveColumns(keys, storageKey = STORAGE_KEY) {
  try { localStorage.setItem(storageKey, JSON.stringify(keys)); } catch {}
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Pure helpers — safe to call from TrackRow (defined outside TrackTable)
function getDisplayValue(track, colKey, trackOverrides) {
  const overrides = trackOverrides[track.id] || {};
  if (overrides[colKey] !== undefined) return overrides[colKey];
  const col = ALL_COLUMNS.find(c => c.key === colKey);
  return col?.render(track) ?? '';
}

function trackIsLoved(track, lovedState) {
  if (lovedState[track.id] !== undefined) return lovedState[track.id];
  return track.is_loved;
}

function cellStyle(col, extra = {}) {
  return {
    width: col.width,
    minWidth: col.width,
    flexShrink: 0,
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    ...extra,
  };
}

function ColumnPicker({ activeKeys, onChange, deletable, storageKey = STORAGE_KEY }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (key) => {
    if (LOCKED_KEYS.includes(key)) return;
    const next = activeKeys.includes(key)
      ? activeKeys.filter(k => k !== key)
      : [...activeKeys, key];
    const ordered = ALL_COLUMNS.map(c => c.key).filter(k => next.includes(k));
    onChange(ordered);
    saveColumns(ordered, storageKey);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}
        onClick={() => setOpen(o => !o)}>⚙ Columns</button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '110%', zIndex: 100,
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '8px 0', minWidth: 200,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}>
          <div style={{
            padding: '6px 16px 8px', fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.1em',
            color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', marginBottom: 4,
          }}>Show / Hide Columns</div>
          {ALL_COLUMNS.map(col => {
            const locked = LOCKED_KEYS.includes(col.key);
            const active = activeKeys.includes(col.key);
            return (
              <label key={col.key} style={{
                display: 'flex', alignItems: 'center', padding: '6px 16px',
                fontSize: 13, cursor: locked ? 'default' : 'pointer',
                color: locked ? 'var(--text-muted)' : 'var(--text)',
                opacity: locked ? 0.6 : 1, userSelect: 'none',
              }}>
                <input type="checkbox" checked={active} disabled={locked}
                  onChange={() => toggle(col.key)} style={{ marginRight: 8 }} />
                {col.key === 'delete'
                  ? (deletable === 'library' ? 'Delete' : 'Remove')
                  : col.label}
                {locked && <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>always on</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BulkEditModal({ tracks, onSave, onClose }) {
  const [genre, setGenre] = useState('');
  const [artist, setArtist] = useState('');
  const [album, setAlbum] = useState('');
  const [year, setYear] = useState('');
  // null = no change, true = love all, false = unlove all
  const [lovedAction, setLovedAction] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const track of tracks) {
        let patchData = {};
        if (genre) {
          const { data } = await findOrCreateGenre(genre);
          patchData.genre = data.id;
        }
        if (artist) {
          const { data } = await findOrCreateArtist(artist);
          patchData.artist = data.id;
        }
        if (album) {
          const { data } = await findOrCreateAlbum(album, track.artist);
          patchData.album = data.id;
        }
        if (year) patchData.year = parseInt(year) || null;
        if (lovedAction !== null) patchData.is_loved = lovedAction;
        if (Object.keys(patchData).length > 0) {
          await updateTrack(track.id, patchData);
        }
      }
      onSave();
    } catch (e) {
      console.error('Bulk save failed', e);
    }
    setSaving(false);
    onClose();
  };

  const lovedBtnStyle = (active, color) => ({
    flex: 1, padding: '7px 0', borderRadius: 6, cursor: 'pointer', fontSize: 18,
    border: `1px solid ${active ? color : 'var(--border)'}`,
    background: active ? color + '22' : 'var(--bg3)',
    color: active ? color : 'var(--text-muted)',
    transition: 'all 0.15s',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 28, minWidth: 380,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>Edit {tracks.length} tracks</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 20px' }}>
          Leave blank to keep existing value
        </p>

        {[
          { label: 'Genre', value: genre, set: setGenre },
          { label: 'Artist', value: artist, set: setArtist },
          { label: 'Album', value: album, set: setAlbum },
          { label: 'Year', value: year, set: setYear, type: 'number' },
        ].map(({ label, value, set, type }) => (
          <div key={label} style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
              {label}
            </label>
            <input
              type={type || 'text'}
              value={value}
              onChange={e => set(e.target.value)}
              placeholder={`Keep existing ${label.toLowerCase()}`}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'var(--bg3)', border: '1px solid var(--border)',
                borderRadius: 6, color: 'var(--text)', fontSize: 13,
                padding: '7px 10px', outline: 'none',
              }}
            />
          </div>
        ))}

        {/* Favorite toggle */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
            Favorite
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={lovedBtnStyle(lovedAction === true, '#f472b6')}
              onClick={() => setLovedAction(lovedAction === true ? null : true)}
              title="Love all selected tracks"
            >
              ♥ Love all
            </button>
            <button
              style={lovedBtnStyle(lovedAction === false, 'var(--text-muted)')}
              onClick={() => setLovedAction(lovedAction === false ? null : false)}
              title="Unlove all selected tracks"
            >
              ♡ Unlove all
            </button>
          </div>
          {lovedAction === null && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              No change — select an option to update
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : `Save ${tracks.length} tracks`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContextMenu({ x, y, track, selectedTracks, onBulkEdit, onClose, editable }) {
  const ref = useRef(null);
  const [lastPlaylists, setLastPlaylists] = useState(getLastPlaylists);

  const { data } = useQuery(
    ['playlists-context'],
    () => getPlaylists({ page_size: 1000 }).then(r => r.data.results || r.data || []),
    { staleTime: 60000 }
  );
  const playlists = (data || []).filter(p => !p.is_smart);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const keyHandler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  const menuWidth = 240;
  const menuHeight = 400;
  const adjustedX = x + menuWidth > window.innerWidth ? x - menuWidth : x;
  const adjustedY = y + menuHeight > window.innerHeight ? y - menuHeight : y;

  const handleAdd = async (playlist) => {
    const tracks = bulkCount > 1 ? selectedTracks : [track];
    try {
      if (tracks.length === 1) {
        await addTrackToPlaylist(playlist.id, tracks[0].id);
      } else {
        const res = await addTracksBulkToPlaylist(playlist.id, tracks.map(t => t.id));
        const { added, skipped } = res.data;
        const msg = skipped > 0
          ? `Added ${added} track${added !== 1 ? 's' : ''} to "${playlist.name}" (${skipped} already there, skipped)`
          : `Added ${added} track${added !== 1 ? 's' : ''} to "${playlist.name}"`;
        alert(msg);
      }
      saveLastPlaylists(playlist, lastPlaylists);
      setLastPlaylists([playlist, ...lastPlaylists.filter(p => p.id !== playlist.id)].slice(0, 3));
    } catch (e) {
      console.error('Failed to add track(s) to playlist', e);
      alert('Failed to add tracks to playlist. Please try again.');
    }
    onClose();
  };

  const handleNewPlaylist = async () => {
    const name = window.prompt('New playlist name:');
    if (!name?.trim()) return;
    try {
      const res = await createPlaylist({ name: name.trim(), is_smart: false });
      const newPlaylist = res.data;
      await handleAdd(newPlaylist);
    } catch (e) {
      alert('Failed to create playlist.');
    }
  };

  const otherPlaylists = playlists.filter(p => !lastPlaylists.find(lp => lp.id === p.id));
  const bulkCount = selectedTracks.length;

  return (
    <div ref={ref} style={{
      position: 'fixed', left: adjustedX, top: adjustedY, zIndex: 9999,
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 10, minWidth: menuWidth, maxHeight: menuHeight,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {bulkCount > 1 && editable && (
        <>
          <div
            onClick={() => { onBulkEdit(); onClose(); }}
            style={{
              padding: '10px 14px', fontSize: 13, cursor: 'pointer',
              color: 'var(--accent)', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            ✏️ Edit {bulkCount} selected tracks
          </div>
          <div style={{ borderBottom: '1px solid var(--border)', margin: '2px 0' }} />
        </>
      )}

      <div style={{
        padding: '8px 14px', fontSize: 11, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>Add to Playlist</div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {/* New Playlist */}
        <div
          onClick={handleNewPlaylist}
          style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          ＋ New Playlist
        </div>
        <div style={{ borderBottom: '1px solid var(--border)', margin: '2px 0' }} />

        {lastPlaylists.length > 0 && (
          <>
            {lastPlaylists.map(lp => (
              <div key={lp.id} onClick={() => handleAdd(lp)} style={{
                padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 8,
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontSize: 10 }}>★</span> {lp.name}
              </div>
            ))}
            <div style={{ borderBottom: '1px solid var(--border)', margin: '2px 0' }} />
          </>
        )}
        {otherPlaylists.map(p => (
          <div key={p.id} onClick={() => handleAdd(p)} style={{
            padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: 'var(--text)',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {p.name}
          </div>
        ))}
        {playlists.length === 0 && (
          <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
            No playlists found
          </div>
        )}
      </div>
    </div>
  );
}

function EditableCell({ value, colKey, track, trackIndex, onSave, editable, activeColKey, activeRowIndex, onActivate, onNavigate }) {
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);
  const isActive = activeRowIndex === trackIndex && activeColKey === colKey;

  useEffect(() => { setEditValue(value); }, [value]);

  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isActive]);

  if (!editable || !EDITABLE_KEYS.includes(colKey)) {
    return <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>;
  }

  if (isActive) {
    return (
      <input
        ref={inputRef}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onBlur={() => {
          if (editValue !== value) onSave(colKey, editValue, track);
          onActivate(null, null);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (editValue !== value) onSave(colKey, editValue, track);
            onNavigate('down', trackIndex, colKey);
          } else if (e.key === 'Tab') {
            e.preventDefault();
            if (editValue !== value) onSave(colKey, editValue, track);
            onNavigate(e.shiftKey ? 'left' : 'right', trackIndex, colKey);
          } else if (e.key === 'Escape') {
            setEditValue(value);
            onActivate(null, null);
          }
        }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg3)', border: '1px solid var(--accent)',
          borderRadius: 4, color: 'var(--text)', fontSize: 13,
          padding: '2px 6px', width: '100%', outline: 'none',
        }}
      />
    );
  }

  return (
    <span
      onClick={e => { e.stopPropagation(); onActivate(trackIndex, colKey); }}
      title="Click to edit"
      style={{ cursor: 'text', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {value || '—'}
    </span>
  );
}

// Stable index cell with hover play button
function VirtualIndexCell({ track, index, sorted, currentTrack, playQueue, isPlaying, col, isMobile }) {
  const [hovered, setHovered] = useState(false);

  if (isMobile) {
    const mobileCol = { ...col, width: 58, minWidth: 58 };
    return (
      <div
        style={{ ...cellStyle(mobileCol), justifyContent: 'center', padding: '2px', position: 'relative', cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); playQueue(sorted, index); }}
      >
        {track.cover_art_url
          ? <img src={track.cover_art_url} alt="" style={{ width: 50, height: 50, borderRadius: 3, objectFit: 'cover', display: 'block' }} />
          : <div style={{ width: 50, height: 50, borderRadius: 3, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎵</div>
        }
        {isPlaying && (
          <div style={{ position: 'absolute', inset: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', borderRadius: 3 }}>
            <span style={{ color: 'var(--accent)', fontSize: 13 }}>♪</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{ ...cellStyle(col), justifyContent: 'center', cursor: 'pointer' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => { e.stopPropagation(); playQueue(sorted, index); }}
    >
      {isPlaying ? (
        <span style={{ color: 'var(--accent)' }}>♪</span>
      ) : hovered ? (
        <span style={{ color: 'var(--accent)', fontSize: 14 }}>▶</span>
      ) : (
        <span style={{ color: 'var(--text-muted)' }}>{index + 1}</span>
      )}
    </div>
  );
}

// TrackRow must be defined outside TrackTable so react-window gets a stable reference.
// In react-window v2, rowProps values are spread directly as props (no `data` wrapper).
const TrackRow = React.memo(({
  index, style,
  sorted, activeColumns, selectedIds, currentTrack, lovedState,
  trackOverrides, activeCell, editable, deletable,
  handleRowClick, handleContextMenu, handleLove, handleSave, handleDelete,
  setActiveCell, handleNavigate, playQueue, onMarkReviewed, isMobile,
  handleOpenPicker,
}) => {

  const track = sorted[index];
  if (!track) return null;

  const isSelected = selectedIds.has(track.id);
  const isPlaying = currentTrack?.id === track.id;
  const loved = trackIsLoved(track, lovedState);

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--border)',
        background: isSelected ? 'var(--bg3)' : undefined,
        outline: isSelected ? '1px solid var(--selected-border)' : undefined,
        color: isPlaying ? 'var(--accent)' : undefined,
        cursor: 'pointer',
        userSelect: 'none',
        boxSizing: 'border-box',
      }}
      onClick={(e) => handleRowClick(e, track, index)}
      onContextMenu={(e) => handleContextMenu(e, track)}
    >
      {activeColumns.map(col => {
        if (col.key === 'index') {
          return (
            <VirtualIndexCell
              key="index"
              track={track}
              index={index}
              sorted={sorted}
              currentTrack={currentTrack}
              playQueue={playQueue}
              isPlaying={isPlaying}
              col={col}
              isMobile={isMobile}
            />
          );
        }
        if (col.key === 'is_loved') {
          return (
            <div
              key="is_loved"
              style={{ ...cellStyle(col), justifyContent: 'center', cursor: 'pointer' }}
              onClick={(e) => handleLove(e, track)}
            >
              <span style={{ color: loved ? 'var(--accent)' : 'var(--border)', fontSize: 16 }}>♥</span>
            </div>
          );
        }
        if (col.key === 'delete') {
          // Always render a spacer so the header and row widths stay in sync.
          // (The column appears in activeColumns for admins even when nothing is deletable.)
          if (!deletable && !onMarkReviewed) return <div key="delete" style={cellStyle(col)} />;
          return (
            <div key="delete" style={{ ...cellStyle(col), justifyContent: 'center', gap: 4, display: 'flex' }}>
              {onMarkReviewed && (
                <button
                  onClick={(e) => { e.stopPropagation(); onMarkReviewed(track.id); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#4ade80', fontSize: 15,
                    padding: '2px 4px', borderRadius: 4, opacity: 0.6, lineHeight: 1,
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = 1}
                  onMouseLeave={e => e.currentTarget.style.opacity = 0.6}
                  title="Mark as reviewed"
                >
                  ✓
                </button>
              )}
              {deletable && (
                <button
                  onClick={(e) => handleDelete(e, track)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: deletable === 'library' ? '#f87171' : 'var(--text-muted)',
                    fontSize: deletable === 'library' ? 15 : 16,
                    padding: '2px 4px', borderRadius: 4, opacity: 0.5, lineHeight: 1,
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = 1}
                  onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
                  title={deletable === 'library' ? 'Delete from library' : 'Remove from playlist'}
                >
                  {deletable === 'library' ? '🗑' : '✕'}
                </button>
              )}
            </div>
          );
        }
        return (
          <div key={col.key} style={{
            ...cellStyle(col),
            color: col.bold ? 'var(--text)' : 'var(--text-muted)',
            fontWeight: col.bold ? 600 : 400,
          }}>
            <EditableCell
              value={getDisplayValue(track, col.key, trackOverrides)}
              colKey={col.key}
              track={track}
              trackIndex={index}
              onSave={handleSave}
              editable={editable}
              activeColKey={activeCell.colKey}
              activeRowIndex={activeCell.rowIndex}
              onActivate={(rowIndex, colKey) => setActiveCell({ rowIndex, colKey })}
              onNavigate={handleNavigate}
            />
          </div>
        );
      })}

      {/* Add to playlist "+" — always visible, not part of column picker.
          Desktop: opens the same compact popup as right-click (handleContextMenu).
          Mobile:  opens the bottom-sheet picker. */}
      <div style={{ width: 40, minWidth: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isMobile) {
              handleOpenPicker(track);
            } else {
              handleContextMenu(e, track);
            }
          }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', fontSize: 18, padding: '2px 4px',
            borderRadius: 4, opacity: 0.4, lineHeight: 1,
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = 1}
          onMouseLeave={e => e.currentTarget.style.opacity = 0.4}
          title="Add to playlist"
        >
          ＋
        </button>
      </div>
    </div>
  );
});

export default function TrackTable({
  tracks = [],
  queryKey,
  editable = false,
  deletable = null,
  playlistId = null,
  onSort = null,
  onSave = null,
  onShuffle = null,       // optional override: () => Track[] — replaces default random shuffle
  serverOrdering = null,
  fetchNextPage = null,
  hasNextPage = false,
  isFetchingMore = false,
  listHeight: listHeightProp = null,
  storageKey: storageKeyProp = null,
  defaultColumns = null,
  onMarkReviewed = null,
  onSelectionChange = null,
}) {
  const effectiveStorageKey = storageKeyProp || STORAGE_KEY;
  const [activeKeys, setActiveKeys] = useState(() => loadColumns(effectiveStorageKey, defaultColumns));
  const [lovedState, setLovedState] = useState({});
  const [trackOverrides, setTrackOverrides] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [activeCell, setActiveCell] = useState({ rowIndex: null, colKey: null });
  const [bulkEditTracks, setBulkEditTracks] = useState(null);
  const [pickerTrack, setPickerTrack] = useState(null);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const { currentTrack, playQueue } = usePlayerStore();
  const { user } = useAuthStore();
  const isAdmin = user?.is_staff === true;
  const queryClient = useQueryClient();

  // Use client-side sort only when no server sort is provided (e.g. playlist pages)
  const { sorted: clientSorted, handleSort, SortIcon } = useSortable(onSort ? [] : tracks, null);
  const sorted = onSort ? tracks : clientSorted;

  const activeColumns = ALL_COLUMNS
    .filter(c => activeKeys.includes(c.key))
    .filter(c => c.key !== 'is_loved' || isAdmin)
    .filter(c => c.key !== 'rating' || isAdmin)
    .filter(c => c.key !== 'delete' || isAdmin || deletable === 'playlist' || onMarkReviewed);
  const visibleEditableKeys = activeColumns.map(c => c.key).filter(k => EDITABLE_KEYS.includes(k));

  // Total pixel width of all rendered columns (active cols + fixed +PL col).
  // Applied to both the header row and the List so they share the same scroll width
  // and don't scroll independently on mobile.
  const PL_COL_WIDTH = 40;
  const totalRowWidth = activeColumns.reduce((sum, col) => sum + col.width, 0) + PL_COL_WIDTH;

  // Dynamic list height
  useEffect(() => {
    const handler = () => {
      setWindowHeight(window.innerHeight);
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const listHeight = listHeightProp ?? Math.max(200, windowHeight - 340);

  // Clear selection when tracks change
  useEffect(() => { setSelectedIds(new Set()); }, [tracks]);

  // Notify parent when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(sorted.filter(t => selectedIds.has(t.id)));
    }
  }, [selectedIds]); // intentionally omitting onSelectionChange from deps — stable callback

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'a' && editable) {
        e.preventDefault();
        setSelectedIds(new Set(sorted.map(t => t.id)));
        return;
      }
      if (e.key === 'Escape') {
        setSelectedIds(new Set());
        setActiveCell({ rowIndex: null, colKey: null });
        return;
      }
      if (activeCell.rowIndex !== null) return;
      if (selectedIds.size === 0) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const allIndices = [...selectedIds]
          .map(id => sorted.findIndex(t => t.id === id))
          .filter(i => i !== -1);
        const anchorIdx = e.key === 'ArrowDown' ? Math.max(...allIndices) : Math.min(...allIndices);
        const nextIdx = e.key === 'ArrowDown'
          ? Math.min(anchorIdx + 1, sorted.length - 1)
          : Math.max(anchorIdx - 1, 0);
        if (e.shiftKey) {
          setSelectedIds(prev => { const next = new Set(prev); next.add(sorted[nextIdx].id); return next; });
        } else {
          setSelectedIds(new Set([sorted[nextIdx].id]));
        }
        setLastSelectedIndex(nextIdx);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [sorted, editable, selectedIds, activeCell]);

  const loveMutation = useMutation(
    (trackId) => loveTrack(trackId),
    {
      onSuccess: (data, trackId) => {
        setLovedState(prev => ({ ...prev, [trackId]: data.data.is_loved }));
        if (queryKey) queryClient.invalidateQueries(queryKey);
      }
    }
  );

  const handleLove = useCallback((e, track) => {
    if (!isAdmin) return;
    e.stopPropagation();
    setLovedState(prev => ({ ...prev, [track.id]: !trackIsLoved(track, prev) }));
    loveMutation.mutate(track.id);
  }, [isAdmin, loveMutation]);

  const handleRowClick = useCallback((e, track, index) => {
    if (e.target.closest('span[title="Click to edit"]') || e.target.tagName === 'INPUT') return;
    if (e.metaKey || e.ctrlKey) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(track.id)) next.delete(track.id);
        else next.add(track.id);
        return next;
      });
      setLastSelectedIndex(index);
    } else if (e.shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      setSelectedIds(new Set(sorted.slice(start, end + 1).map(t => t.id)));
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(track.id) && next.size === 1) next.delete(track.id);
        else { next.clear(); next.add(track.id); }
        return next;
      });
      setLastSelectedIndex(index);
    }
  }, [lastSelectedIndex, sorted]);

  const handleContextMenu = useCallback((e, track) => {
    e.preventDefault();
    if (!selectedIds.has(track.id)) setSelectedIds(new Set([track.id]));
    setContextMenu({ x: e.clientX, y: e.clientY, track });
  }, [selectedIds]);

  const handleSave = useCallback(async (colKey, newValue, track) => {
    try {
      let patchData = {};
      // isRename = true when findOrCreate returns the SAME entity the track
      // already belongs to (the backend renamed it in-place, e.g. casing fix).
      // In that case every track sharing the old display value needs updating.
      // isRename = false means the track was moved to a different entity — only
      // the edited track's UI should change.
      let isRename = false;

      if (colKey === 'title') {
        patchData = { title: newValue };
      } else if (colKey === 'track_number') {
        patchData = { track_number: parseInt(newValue) || null };
      } else if (colKey === 'year') {
        patchData = { year: parseInt(newValue) || null };
      } else if (colKey === 'genre_name') {
        const { data: genreData } = await findOrCreateGenre(newValue);
        patchData = { genre: genreData.id };
        isRename = genreData.id === track.genre;
      } else if (colKey === 'artist_name') {
        const { data: artistData } = await findOrCreateArtist(newValue);
        patchData = { artist: artistData.id };
        isRename = artistData.id === track.artist;
      } else if (colKey === 'album_title') {
        const { data: albumData } = await findOrCreateAlbum(newValue, track.artist);
        patchData = { album: albumData.id };
        isRename = albumData.id === track.album;
      }
      await updateTrack(track.id, patchData);

      if (isRename) {
        // Entity renamed in-place — update every visible track that shared
        // the old display value so they all stay in sync immediately.
        const oldValue = track[colKey];
        setTrackOverrides(prev => {
          const next = { ...prev };
          sorted.forEach(t => {
            if (t[colKey] === oldValue) {
              next[t.id] = { ...next[t.id], [colKey]: newValue };
            }
          });
          return next;
        });
      } else {
        // Track moved to a different entity — only this row needs updating.
        setTrackOverrides(prev => ({ ...prev, [track.id]: { ...prev[track.id], [colKey]: newValue } }));
      }

      if (queryKey) queryClient.invalidateQueries(queryKey);
      onSave?.();
    } catch (e) {
      console.error('Save failed', e);
    }
  }, [queryKey, queryClient, onSave, sorted]);

  const handleNavigate = useCallback((direction, rowIndex, colKey) => {
    const colIdx = visibleEditableKeys.indexOf(colKey);
    if (direction === 'right') {
      if (colIdx < visibleEditableKeys.length - 1) {
        setActiveCell({ rowIndex, colKey: visibleEditableKeys[colIdx + 1] });
      } else if (rowIndex < sorted.length - 1) {
        setActiveCell({ rowIndex: rowIndex + 1, colKey: visibleEditableKeys[0] });
      }
    } else if (direction === 'left') {
      if (colIdx > 0) {
        setActiveCell({ rowIndex, colKey: visibleEditableKeys[colIdx - 1] });
      } else if (rowIndex > 0) {
        setActiveCell({ rowIndex: rowIndex - 1, colKey: visibleEditableKeys[visibleEditableKeys.length - 1] });
      }
    } else if (direction === 'down') {
      if (rowIndex < sorted.length - 1) {
        setActiveCell({ rowIndex: rowIndex + 1, colKey });
      } else {
        setActiveCell({ rowIndex: null, colKey: null });
      }
    }
  }, [visibleEditableKeys, sorted.length]);

  const handleDelete = useCallback(async (e, track) => {
    e.stopPropagation();
    if (deletable === 'library') {
      if (!window.confirm(`Delete "${track.title}" from your library? This cannot be undone.`)) return;
      try {
        await deleteTrack(track.id);
        if (queryKey) queryClient.invalidateQueries(queryKey);
        onSave?.();
      } catch (err) {
        console.error('Delete failed', err);
        alert('Failed to delete track. Please try again.');
      }
    } else if (deletable === 'playlist' && playlistId) {
      try {
        await removeTrackFromPlaylist(playlistId, track.id);
        if (queryKey) queryClient.invalidateQueries(queryKey);
        onSave?.();
      } catch (err) {
        console.error('Remove from playlist failed', err);
        alert(`Failed to remove track: ${err.response?.data?.detail || err.message}`);
      }
    }
  }, [deletable, playlistId, queryKey, queryClient, onSave]);

  const handleOpenPicker = useCallback((track) => setPickerTrack(track), []);

  // Stable itemData for react-window — only recreates when actual data changes
  const itemData = useMemo(() => ({
    sorted,
    activeColumns,
    selectedIds,
    currentTrack,
    lovedState,
    trackOverrides,
    activeCell,
    editable,
    deletable,
    handleRowClick,
    handleContextMenu,
    handleLove,
    handleSave,
    handleDelete,
    setActiveCell,
    handleNavigate,
    playQueue,
    visibleEditableKeys,
    onMarkReviewed,
    isMobile,
    handleOpenPicker,
  }), [
    sorted, activeColumns, selectedIds, currentTrack, lovedState, trackOverrides,
    activeCell, editable, deletable, handleRowClick, handleContextMenu, handleLove,
    handleSave, handleDelete, handleNavigate, playQueue, visibleEditableKeys, onMarkReviewed,
    isMobile, handleOpenPicker,
  ]);

  // Infinite scroll — load next page when user approaches the bottom
  const handleRowsRendered = useCallback(({ stopIndex }) => {
    if (!fetchNextPage || !hasNextPage || isFetchingMore) return;
    if (stopIndex >= sorted.length - 20) fetchNextPage();
  }, [sorted.length, hasNextPage, isFetchingMore, fetchNextPage]);

  // Column header sort handler
  const handleHeaderSort = useCallback((col) => {
    if (!col.sortKey) return;
    if (onSort) {
      onSort(col.sortKey);
    } else {
      handleSort(col.sortKey);
    }
  }, [onSort, handleSort]);

  // Sort icon for server-side sort mode — supports multi-column sort history array
  const ServerSortIcon = useCallback(({ field }) => {
    const serverField = SORT_KEY_MAP[field] || field;
    const ordering = Array.isArray(serverOrdering) ? serverOrdering : (serverOrdering ? [serverOrdering] : []);
    const primaryRaw = ordering[0] ?? '';
    const primaryField = primaryRaw.replace(/^-/, '');
    const dir = primaryRaw.startsWith('-') ? 'desc' : 'asc';

    if (primaryField === serverField) {
      return <span style={{ color: 'var(--accent)', marginLeft: 4 }}>{dir === 'asc' ? '↑' : '↓'}</span>;
    }
    const secondaryIdx = ordering.findIndex((f, i) => i > 0 && f.replace(/^-/, '') === serverField);
    if (secondaryIdx > 0) {
      return <span style={{ color: 'var(--text-muted)', marginLeft: 3, fontSize: 9, verticalAlign: 'super' }}>{secondaryIdx + 1}</span>;
    }
    return <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>⇅</span>;
  }, [serverOrdering]);

  const selectedTracks = sorted.filter(t => selectedIds.has(t.id));

  return (
    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 12px' }}
          onClick={() => playQueue(sorted, 0)} disabled={!sorted.length}>
          ▶ Play
        </button>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}
          onClick={() => playQueue(onShuffle ? onShuffle() : shuffleArray(sorted), 0)} disabled={!sorted.length}>
          ⇄ Shuffle
        </button>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          {sorted.length} tracks
          {selectedIds.size > 0 && (
            <span style={{ color: 'var(--accent)', marginLeft: 8 }}>
              · {selectedIds.size} selected
            </span>
          )}
        </span>
        {selectedIds.size > 1 && editable && (
          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}
            onClick={() => setBulkEditTracks(selectedTracks)}>
            ✏️ Edit {selectedIds.size} tracks
          </button>
        )}
        {selectedIds.size > 0 && (
          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}
            onClick={() => setSelectedIds(new Set())}>
            ✕ Clear
          </button>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <ColumnPicker activeKeys={activeKeys} onChange={setActiveKeys} deletable={deletable} storageKey={effectiveStorageKey} />
        </div>
      </div>

      {/* Virtual table */}
      <div className="track-table-scroll-container" style={{ userSelect: 'none' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          background: 'var(--bg)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          height: 32,
          minWidth: totalRowWidth,
        }}>
          {activeColumns.map(col => (
            <div
              key={col.key}
              style={{
                width: col.width,
                minWidth: col.width,
                flexShrink: 0,
                padding: '0 8px',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-muted)',
                cursor: col.sortKey ? 'pointer' : 'default',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => handleHeaderSort(col)}
            >
              {col.key === 'delete'
                ? (deletable === 'library' ? 'Delete' : 'Remove')
                : col.label}
              {col.sortKey && (onSort
                ? <ServerSortIcon field={col.sortKey} />
                : <SortIcon field={col.sortKey} />
              )}
            </div>
          ))}
          {/* Header cell for the always-visible "+" column */}
          <div style={{
            width: 40, minWidth: 40, flexShrink: 0,
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.05em', color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', padding: '0 4px',
          }}>
            +PL
          </div>
        </div>

        {/* Rows — virtual list */}
        <List
          rowComponent={TrackRow}
          rowCount={sorted.length}
          rowHeight={isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT}
          rowProps={itemData}
          style={{ height: listHeight, minWidth: totalRowWidth }}
          onRowsRendered={handleRowsRendered}
        />

        {isFetchingMore && (
          <div style={{ textAlign: 'center', padding: '8px', fontSize: 12, color: 'var(--text-muted)' }}>
            Loading more…
          </div>
        )}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          track={contextMenu.track}
          selectedTracks={selectedTracks}
          onBulkEdit={() => setBulkEditTracks(selectedTracks)}
          onClose={() => setContextMenu(null)}
          editable={editable}
        />
      )}

      {bulkEditTracks && (
        <BulkEditModal
          tracks={bulkEditTracks}
          onSave={() => { if (queryKey) queryClient.invalidateQueries(queryKey); setSelectedIds(new Set()); }}
          onClose={() => setBulkEditTracks(null)}
        />
      )}

      {pickerTrack && (
        <PlaylistPicker track={pickerTrack} onClose={() => setPickerTrack(null)} />
      )}
    </div>
  );
}
